import { useState, useEffect, useMemo } from 'react';
import { apiDeployJob, apiCheckJob, apiCheckAuthorization, usageStatsIncrement } from '../helpers/apiHelpers';
import { useLocation } from 'react-router-dom';
import { useStableAccessToken } from './useStableAccessToken';

const accessDeniedMessage = 
  'Not authorized to deploy. Please request access via https://euewizard.docs.teladoc.dev/#/pages/dev?id=access-to-controller';

export function useDeployment() {
  const tokenGetter = useStableAccessToken();
  const [authError, setAuthError] = useState<string | null>(null);
  const [disableDeploy, setDisableDeploy] = useState(false);
  const [jobUrl, setJobUrl] = useState<string | null>(null);
  const [deployError, setDeployError] = useState<string | null>(null);
  const [isDeploying, setIsDeploying] = useState(false);
  const [clusterAuthError, setClusterAuthError] = useState<string | null>(null);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [slotsRemaining, setSlotsRemaining] = useState<number>(Infinity);
  const [skipQuota, setSkipQuota] = useState(false);
  const [classError, setClassError] = useState<string | null>(null);
  const [isEligible, setIsEligible] = useState<boolean>(false);
  const [queue, setQueue] = useState<number>(0)
  const [message, setMessage] = useState<string>('')
  const [title, setTitle] = useState<string>('Triggering deployment job')

  // This function triggers the deployment job using the helper.
  const handleDeploy = async () => {
    try {
      setDeployError(null);
      setJobUrl(null);
      setIsSuccessModalOpen(true);
      setIsDeploying(true);
      setAuthError(null);

      // 1) Read final user selection from localStorage (since your ReviewStep effect wrote it)
      const reviewData = JSON.parse(localStorage.getItem('reviewData') || '{}');

      // 2) usageStatsIncrement => pass an array of the service names
      const chosenServices = reviewData.selectedDependencies.map((d: any) => d.name);
      const chosenExperiences = reviewData.selectedBusinessUnits || [];

      await usageStatsIncrement(chosenServices, chosenExperiences, tokenGetter);
      console.log('Usage stats increment triggered for:', chosenServices, chosenExperiences)

      const authorized = await apiCheckAuthorization(tokenGetter);
      if (!authorized) {
        setAuthError(accessDeniedMessage);
        setIsDeploying(false);
        return;
      }
      // 3) Trigger the deployment job
      // DEPLOY_JOB_V2_TRIGGERED is fired automatically by the backend audit middleware
      // when POST /api/deployment/deploy-job/v2 is called, so no duplicate FE event needed.
      const response = await apiDeployJob(tokenGetter);
      if (response.status === 'triggered') {
        setJobUrl(jobUrl);
        setIsSuccessModalOpen(true);
        setIsDeploying(false);
      } else if (response.status === 'queued') {
        const queue_id = response.id;
        setQueue(queue_id)
        setTitle('Awaiting deployment job url')
        setMessage(`Fetching deployment job url. This operation may take a while.`)
        
        do {
          const item = await apiCheckJob(queue_id, tokenGetter)
          if (item.status === 'triggered') {
            setJobUrl(item.jobUrl)
            setIsDeploying(false)
            break;
          } else if (item.status === 'cancelled') {
            setIsDeploying(false);
            setDeployError('The job was cancelled')
            break;
          } else if (item.status === 'unknown') {
            setIsDeploying(false);
            setDeployError('The job status is unknown')
            break;
          } else if (item.status === 'blocked') {
            setTitle('Pending to start')
            setMessage(`The job is blocked, please wait. Reason: ${item.reason}`)
          } else if (item.status === 'queued') {
            if (item.reason) {
              setMessage(`Fetching deployment job url. This operation may take a while (${item.reason})`)
            }
          }
          await new Promise(resolve => setTimeout(resolve, 2500));

        } while (true);
      }

    } catch (error: any) {
      console.error('Deployment job failed:', error);
      setDeployError(error.message || 'Failed to deploy environment.');
      setIsDeploying(false);
    }
  };


  // Check eligibility
  useEffect(() => {
    const enabled = localStorage.getItem('quick_deploy_enabled') === 'true';
    setIsEligible(enabled);
    setIsEligible(true);  // temporary enabled for testing
  }, []);

  return {
    // State
    authError,
    setAuthError,
    disableDeploy,
    setDisableDeploy,
    jobUrl,
    setJobUrl,
    deployError,
    setDeployError,
    isDeploying,
    setIsDeploying,
    clusterAuthError,
    setClusterAuthError,
    isSuccessModalOpen,
    setIsSuccessModalOpen,
    slotsRemaining,
    setSlotsRemaining,
    skipQuota,
    setSkipQuota,
    classError,
    setClassError,
    isEligible,
    setIsEligible,
    message,
    title,
    queue,

    // Actions
    handleDeploy,
  };
}
